import { directionFingerprint } from '@src/services/dubbing/directionPrefill';
import {
  planPreview,
  undirectedReplyNotice,
} from '@src/services/dubbing/directionPreview';
import { useStore } from '@src/stores-v2/StoreContext';
import { MuxPauseIcon } from '@src/svgs/MuxPauseIcon';
import { MuxPlayIcon } from '@src/svgs/MuxPlayIcon';
import { extractErrorInfo, isAbortError } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { SamplePlayButton, SampleUnavailable } from './samplePlayStyles';

import type { DirectionPreview } from '@src/services/dubbing/directionPreview';

const logger = LogManager.getLogger('aiVoices:DirectedPreviewButton');

/**
 * PLAT-12023 — the play button on a dub speaker's row, playing what the RUN
 * would produce for that speaker: their own words, in their voice, with the
 * current direction applied. The look of `LanguageSamplePlayButton` (the quiet
 * play glyph), in the same place; what it plays is decided by `planPreview`:
 *
 * - no words known (a language's first dub) → the stored sample, as before;
 *   with a direction, the tooltip says the sample does not follow it;
 * - words known → an audition of those words (`voiceTest`), with the direction
 *   when there is one;
 * - a direction this voice's model cannot take → disabled, with the reason as
 *   its tooltip. Never the stock sample in place of a directed preview.
 *
 * Each speaker previews on their own, and only one plays at a time: pressing
 * one row's button stops another's.
 *
 * A server that does not apply directions to previews yet (PLAT-12028) plays
 * the words undirected; the result says whether a direction was applied, and
 * when one was sent but not applied the row says so under the button.
 */
interface DirectedPreviewButtonProps {
  aiVoiceId: string;
  /** BCP-47 language code of the run. */
  language: string;
  preview: DirectionPreview;
  disabled?: boolean;
  /** Diameter in px; 28 fits a picker row. */
  size?: number;
  /**
   * PLAT-12042 — the prominent weight (`samplePlayStyles`): for a play that
   * is the only control in its region and the thing the region is for, as in
   * the Redub Selection popover's speaker row.
   */
  prominent?: boolean;
}

/** The one preview playing anywhere, so a second press elsewhere stops it. */
let currentAudio: HTMLAudioElement | null = null;

const DirectedPreviewButton = ({
  aiVoiceId,
  language,
  preview,
  disabled = false,
  size = 28,
  prominent = false,
}: DirectedPreviewButtonProps) => {
  const { datoClientStore } = useStore();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  // The last synthesis, kept with the question it answered, so pressing play
  // again on the same words and direction replays rather than re-synthesizes
  // — and a moved slider is a new question.
  const [resolved, setResolved] = useState<{
    key: string;
    url: string;
    notice: string | null;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [failed, setFailed] = useState(false);

  const plan = planPreview(preview);
  const key = [
    aiVoiceId,
    language,
    plan.kind,
    plan.kind === 'audition' ? plan.text : '',
    plan.kind === 'audition' && plan.direction
      ? directionFingerprint(plan.direction)
      : '',
  ].join('\u0000');

  const stop = () => {
    audioRef.current?.pause();
    audioRef.current = null;
    setPlaying(false);
  };

  // A change of voice or language forgets what was resolved; a change of
  // direction is handled by the key, so the audio of the old direction is
  // never replayed as the new one.
  useEffect(() => {
    stop();
    setResolved(null);
    setUnavailable(false);
    setFailed(false);
  }, [aiVoiceId, language]);

  // Stop playback and any request on unmount, so a preview cannot keep
  // playing after its row or modal is gone.
  useEffect(
    () => () => {
      abortRef.current?.abort();
      audioRef.current?.pause();
      if (currentAudio === audioRef.current) currentAudio = null;
    },
    [],
  );

  const play = (src: string) => {
    if (currentAudio && currentAudio !== audioRef.current) {
      currentAudio.pause();
    }
    const audio = new Audio(src);
    audioRef.current = audio;
    currentAudio = audio;
    audio.onended = () => setPlaying(false);
    audio.onpause = () => setPlaying(false);
    audio
      .play()
      .then(() => setPlaying(true))
      .catch(err => {
        logger.warn('preview play failed', extractErrorInfo(err));
        setPlaying(false);
      });
  };

  const handleClick = async () => {
    if (playing) {
      stop();
      return;
    }
    if (plan.kind === 'refused') return;
    if (resolved?.key === key) {
      play(resolved.url);
      return;
    }
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setFailed(false);
    try {
      if (plan.kind === 'sample') {
        const result =
          await datoClientStore.dubbingRepository.getVoiceSampleUrl({
            aiVoiceId,
            language,
            signal: controller.signal,
          });
        if (controller.signal.aborted) return;
        if (!result) {
          setUnavailable(true);
          return;
        }
        setResolved({ key, url: result.audioUrl, notice: null });
        play(result.audioUrl);
        return;
      }
      const result = await datoClientStore.dubbingRepository.voiceTest({
        aiVoiceId,
        language,
        text: plan.text,
        ...(plan.direction ? { direction: plan.direction } : {}),
        signal: controller.signal,
      });
      if (controller.signal.aborted) return;
      setResolved({
        key,
        url: result.audioUrl,
        notice: undirectedReplyNotice(plan.direction, !!result.direction),
      });
      play(result.audioUrl);
    } catch (err) {
      if (isAbortError(err)) return;
      logger.error('preview failed', { aiVoiceId, ...extractErrorInfo(err) });
      setFailed(true);
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  };

  if (unavailable) {
    return (
      <SampleUnavailable $size={size} title="No sample generated yet">
        —
      </SampleUnavailable>
    );
  }

  const refused = plan.kind === 'refused';
  const title = refused
    ? plan.reason
    : failed
      ? 'Couldn’t make the preview. Try again.'
      : plan.hint;
  const notice = resolved?.key === key ? resolved.notice : null;

  return (
    <Wrap>
      <SamplePlayButton
        type="button"
        $size={size}
        $playing={playing}
        $prominent={prominent}
        onClick={() => void handleClick()}
        disabled={disabled || loading || refused}
        aria-disabled={refused || undefined}
        aria-label={playing ? 'Pause preview' : 'Play preview'}
        title={title}
        data-testid="dub-direction-preview"
      >
        {playing ? (
          <MuxPauseIcon width="12" height="12" fillColor="currentColor" />
        ) : (
          <MuxPlayIcon width="12" height="12" fillColor="currentColor" />
        )}
      </SamplePlayButton>
      {notice && (
        <Notice role="status" data-testid="dub-direction-preview-notice">
          {notice}
        </Notice>
      )}
    </Wrap>
  );
};

export default DirectedPreviewButton;

// The button keeps its circle; the notice, when there is one, hangs under the
// row's controls as the field-level hint style, so the row stays one line
// until something has to be said.
const Wrap = styled.span`
  position: relative;
  display: inline-flex;
  flex-shrink: 0;
`;

const Notice = styled.span`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 2px;
  font-size: 11px;
  line-height: var(--leading-text-xs);
  color: var(--text-muted);
  white-space: nowrap;
`;
